import { ChangeEvent, useState ,useMemo} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Badge, Button, CircularProgress, Grid, IconButton, Popover, Switch, Tooltip, Typography } from "@mui/material";
import { Add, FilterList, Visibility } from "@mui/icons-material";

import { CreateArtefacts } from "../component/CreateArtefacts";
import { ArtefactsDetails } from "../component/ArtefactsDetails";
import { ArtefactsFilterSidebar, ArtefactsFilterValues, emptyArtefactsFilter } from "../component/ArtefactsFilterSidebar";

import { useFetchArtefacts } from "../hooks/useFetchArtefacts";
import { useFetchArtefactsState } from "../hooks/useFetchArtefactsState";

import { BudgetCard, DrawerControl, MetaDrivenTable, PaginationControls, SearchField } from "../../../shared/components";
import { metrics } from "../utils";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { ColumnFilter, FilterOption } from "../../tasks/components/columnFilter";
import { formatDate } from "../../../shared/utils";
import { Artefact } from "../types";

export const Documents: React.FC = () => {
    const { isAllowedAddArtefact, isContractorPerson } = useCapabilities();
    const { projectId } = useParams();
    const [searchParam, setSearchParam] = useSearchParams();
    const [searchDraft, setSearchDraft] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(25);
    const [assignedToMe, setAssignedToMe] = useState(false);
    
    const selected = searchParam.get('selected');
    const id = searchParam.get('id');
    // Column-header filter (status only)
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [filterAnchorEl, setFilterAnchorEl] = useState<Record<string, HTMLElement | null>>({});

    // Drawer filter state
    const [artefactsFilter, setArtefactsFilter] = useState<ArtefactsFilterValues>(emptyArtefactsFilter);
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

    const level = projectId ? "project" : "programme";

    const activeFilterCount =
        (statusFilter.length > 0 ? 1 : 0) +
        (artefactsFilter.category.length > 0 ? 1 : 0) +
        (artefactsFilter.subCategory.length > 0 ? 1 : 0) +
        (artefactsFilter.owner.length > 0 ? 1 : 0) +
        (!!artefactsFilter.requiredDate ? 1 : 0) +
        (!!artefactsFilter.expiryDate ? 1 : 0) +
        (artefactsFilter.portfolio.length > 0 ? 1 : 0) +
        (artefactsFilter.project.length > 0 ? 1 : 0) +
        (artefactsFilter.phase.length > 0 ? 1 : 0);

    const hasActiveFilters = activeFilterCount > 0;

    const handleAssignedToMeChange = () => {
        setAssignedToMe(!assignedToMe);
    };

    const { isPending, artefactsData, artefactsPagination, refetch: refetchArtefacts } = useFetchArtefacts({
        projectId,
        stageGateId: null,
        page,
        limit,
        searchKey,
        assignedToMe,
        statusFilter,
        categoryFilter: artefactsFilter.category,
        subCategoryFilter: artefactsFilter.subCategory,
        ownerFilter: artefactsFilter.owner.map((o) => o.value),
        portfolioFilter: artefactsFilter.portfolio.map((p) => String(p.id)),
        projectFilter: artefactsFilter.project.map((p) => p.value),
        phaseFilter: artefactsFilter.phase,
        dateFrom: artefactsFilter.requiredDate,
        dateTo: artefactsFilter.expiryDate,
    });
    const { isPending: isCountPending, artefactsDataState } = useFetchArtefactsState(projectId);

    const [formStatus, setFormStatus] = useState<{
        open: boolean;
        status: boolean;
        id: null | string;
    }>({
        open: false,
        status: false,
        id: null
    });

    const changePage = (page: number) => {
        setPage(page);
    };

    const changeRowsPerPage = (limit: number) => {
        setPage(1);
        setLimit(limit);
    };

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
    };

    const handleClear = () => {
        setSearchDraft('');
        setSearchKey('');
    };

    const handleSearch = () => {
        setSearchKey(searchDraft);
    };

    const onOpen = (status: boolean, id?: string) => {
        setFormStatus({
            open: true,
            status: status,
            id: id ? id : null
        });
    };

    const onClose = () => {
        setFormStatus({
            open: false,
            status: false,
            id: null
        });
    };

    const onOpenArtefactDetails = async (artefact: { id: string, title: string, description: string }) => {
        const params = new URLSearchParams(searchParam);
        params.set('selected', 'true');
        params.set('id', artefact.id);
        setSearchParam(params, { replace: true });
    };

    const onCloseArtefactDetails = async () => {
        const params = new URLSearchParams(searchParam);
        params.delete('selected');
        params.delete('id');
        setSearchParam(params, { replace: true });
    };

    const { getFilterOptions } = useDisplayField([]);

    const filterTooltipContent = useMemo(() => {
        if (!hasActiveFilters) return "";
        const lines: string[] = [];
        const statusOptions = getFilterOptions("ARTEFACT_STATUS");
        const categoryOptions = getFilterOptions("ARTEFACT_CATEGORY");
        const subCategoryOptions = getFilterOptions("ARTEFACT_SUB_CATEGORY");
        const toLabel = (opts: { key: string; display_text: string }[], key: string) =>
            opts.find((o) => o.key === key)?.display_text ?? key;
        if (statusFilter.length > 0)
            lines.push(`Status: ${statusFilter.map((k) => toLabel(statusOptions, k)).join(", ")}`);
        if (artefactsFilter.owner.length > 0)
            lines.push(`Owner: ${artefactsFilter.owner.map((o) => o.label).join(", ")}`);
        if (artefactsFilter.category.length > 0)
            lines.push(`Category: ${artefactsFilter.category.map((k) => toLabel(categoryOptions, k)).join(", ")}`);
        if (artefactsFilter.subCategory.length > 0)
            lines.push(`Sub Category: ${artefactsFilter.subCategory.map((k) => toLabel(subCategoryOptions, k)).join(", ")}`);
        if (artefactsFilter.requiredDate)
            lines.push(`Required Date: ${formatDate(artefactsFilter.requiredDate)}`);
        if (artefactsFilter.expiryDate)
            lines.push(`Expiry Date: ${formatDate(artefactsFilter.expiryDate)}`);
        if (artefactsFilter.portfolio.length > 0)
            lines.push(`Portfolio: ${artefactsFilter.portfolio.map((p) => p.portfolio_name || p.name || p.label || String(p.id)).join(", ")}`);
        if (artefactsFilter.project.length > 0)
            lines.push(`Project: ${artefactsFilter.project.map((p) => p.label).join(", ")}`);
        if (artefactsFilter.projectPhase.length > 0)
            lines.push(`Project Phase: ${artefactsFilter.projectPhase.join(", ")}`);
        return (
            <Box sx={{ p: 0.5 }}>
                {lines.map((line, i) => (
                    <Typography key={i} variant="caption" display="block">
                        {line}
                    </Typography>
                ))}
            </Box>
        );
    }, [hasActiveFilters, statusFilter, artefactsFilter, getFilterOptions]);

    const predefinedOptions: Record<string, FilterOption[]> = useMemo(() => ({
        status: getFilterOptions('ARTEFACT_STATUS'),
    }), [getFilterOptions]);

    const columnLabels: Record<string, string> = {
        status: 'Status',
    };

    const columnSelectedKeys: Record<string, string[]> = {
        status: statusFilter,
    };

    const createFilterHeader = (label: string, columnKey: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{label}</span>
            {predefinedOptions[columnKey] !== undefined && (
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        const target = e.currentTarget;
                        setFilterAnchorEl((prev) => ({ ...prev, [columnKey]: target }));
                    }}
                    sx={{ p: 0.5, ml: 'auto', pointerEvents: 'auto' }}
                >
                    <Badge
                        variant="dot"
                        invisible={!(columnSelectedKeys[columnKey]?.length > 0)}
                        color="error"
                        sx={{ '& .MuiBadge-dot': { width: 8, height: 8, minWidth: 8 } }}
                    >
                        <FilterList fontSize="small" />
                    </Badge>
                </IconButton>
            )}
        </Box>
    );

    const dynamicHeaders = [
        { alias_name: "id", label: "Artefact", display_order: 1, is_show: true },
        { alias_name: "project_title", label: "Project Title", display_order: 2, is_show: true },
        { alias_name: "sub_category_display_text", label: "Sub Category", display_order: 4, is_show: true },
        { alias_name: "status_display_text", label: createFilterHeader("Status", "status"), display_order: 5, is_show: true },
        { alias_name: "file_status_display_text", label: "File Status", display_order: 6, is_show: true},
        { alias_name: "version_id", label: "Version", display_order: 7, is_show: true },
        { alias_name: "days_since_upload", label: "Days Since Upload", display_order: 8, is_show: true },
        { alias_name: "assigned_to", label: "Owner", display_order: 9, is_show: true },
        { alias_name: "required_date", label: "Required Date", display_order: 10, is_show: true },
        { alias_name: "expiry_date", label: "Expiry Date", display_order: 11, is_show: true },
        { alias_name: "title", label: "", display_order: 12, is_show: false },
        { alias_name: "desc", label: "", display_order: 13, is_show: false },
    ];

    const handleFilterApply = (columnKey: string, keys: string[]) => {
        if (columnKey === 'status') setStatusFilter(keys);
        setFilterAnchorEl(prev => ({ ...prev, [columnKey]: null }));
    };

    if (isPending || isCountPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    const selectedArtefact = artefactsData?.find((a) => String(a.id) === String(id));

    return (
        <Box py={2} display={'flex'} gap={2} flexDirection={'column'} height={'100%'} overflow={'hidden'}>

            <Grid container spacing={2} px={2}>
                {
                    [
                        { label: 'Total Artefacts', value: artefactsDataState?.total, icon: 'Balance', color: 'info' as const },
                        { label: 'Accepted', value: artefactsDataState?.accepted, icon: 'Cost', color: 'green' as const },
                        { label: 'In Review', value: artefactsDataState?.in_review, icon: 'Assignment', color: 'amber' as const },
                        { label: 'Expired', value: artefactsDataState?.file_expired, icon: 'Trending', color: 'red' as const },
                    ].map((value, index) => (
                        <Grid size={3} key={index}>
                            <BudgetCard label={value.label} value={value.value as string} icon={value.icon} color={value.color} removeMoney={true} />
                        </Grid>
                    ))
                }
            </Grid>

            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} px={2}>
                <Box mr={'auto'}>
                    <SearchField searchTerm={searchDraft} handleSearch={handleChangeSearch} handleClearSearch={handleClear} handleApplySearch={handleSearch} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
                    {!isContractorPerson && (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Typography variant="subtitle2">Assigned to Me: </Typography>
                            <Switch checked={assignedToMe} onChange={handleAssignedToMeChange} />
                        </Box>
                    )}
                    <Tooltip
                        title={filterTooltipContent}
                        arrow
                        disableHoverListener={!hasActiveFilters}
                        placement="bottom-start"
                    >
                        <Button
                            startIcon={<FilterList />}
                            variant="text"
                            color="inherit"
                            onClick={() => setIsFilterDrawerOpen(true)}
                        >
                            {hasActiveFilters ? `FILTERS (${activeFilterCount})` : "FILTERS"}
                        </Button>
                    </Tooltip>
                    {
                        isAllowedAddArtefact && (
                            <Button
                                startIcon={<Add />}
                                variant="text"
                                color="primary"
                                onClick={() => { onOpen(false) }}
                            >
                                CREATE ARTEFACT REQUIREMENT
                            </Button>
                        )
                    }
                </Box>
            </Box>

            <MetaDrivenTable
                headers={dynamicHeaders}
                data={artefactsData || []}
                dataMetrics={metrics}
                sortDisabled={true}
                actions={[
                    {
                        label: <Visibility />,
                        handleClick: (row: { id: string, title: string, description: string }) => {
                            onOpenArtefactDetails(row);
                        }
                    }]}
            />

            <Box flexShrink={0}>
                <PaginationControls
                    total={artefactsPagination?.total as number}
                    rowsPerPage={artefactsPagination?.limit as number}
                    currentPage={artefactsPagination?.page as number}
                    handlePageChange={changePage}
                    handleRowsPerPage={changeRowsPerPage}
                />
            </Box>

            <DrawerControl
                title={selectedArtefact?.title || "Artefact Details"}
                subTitle={selectedArtefact?.description || ""}
                isOpen={selected === 'true' && !!id}
                handleClose={onCloseArtefactDetails}
                children={
                    id ? (
                        <ArtefactsDetails
                            projectId={projectId}
                            artefactId={id}
                            refetchArtefacts={refetchArtefacts}
                        />
                    ) : <></>
                }
            />

            <DrawerControl
                title={"Create/Edit Artefact Requirement"}
                subTitle={""}
                isOpen={formStatus.open}
                handleClose={onClose}
                children={
                    <CreateArtefacts
                        projectId={projectId ?? undefined}
                        stageGateId={null}
                        isEdit={formStatus.status}
                        artefactId={formStatus.id}
                        onClose={onClose}
                        onSave={() => {
                            onClose();
                            refetchArtefacts();
                        }}
                    />
                }
            />

            <DrawerControl
                title="Filter Artefacts"
                subTitle="Apply filters to refine results"
                isOpen={isFilterDrawerOpen}
                handleClose={() => setIsFilterDrawerOpen(false)}
            >
                <ArtefactsFilterSidebar
                    level={level}
                    projectId={projectId}
                    values={artefactsFilter}
                    onApply={(vals) => {
                        setArtefactsFilter(vals);
                        setPage(1);
                    }}
                    onClose={() => setIsFilterDrawerOpen(false)}
                />
            </DrawerControl>

            {/* Column filters via Popover */}
            {Object.entries(filterAnchorEl).map(([columnKey, anchorEl]) =>
                anchorEl ? (
                    <Popover
                        key={columnKey}
                        open
                        anchorEl={anchorEl}
                        onClose={() => setFilterAnchorEl(prev => ({ ...prev, [columnKey]: null }))}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        <ColumnFilter
                            columnLabel={columnLabels[columnKey] ?? columnKey}
                            selected={columnSelectedKeys[columnKey] || []}
                            options={predefinedOptions[columnKey] || []}
                            onApply={(keys) => handleFilterApply(columnKey, keys)}
                            onClose={() => setFilterAnchorEl(prev => ({ ...prev, [columnKey]: null }))}
                        />
                    </Popover>
                ) : null
            )}

        </Box>
    )
};